<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-7 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("member_coins.liability.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-lg-4 py-lg-4">
        <template slot="header">
          <div class="row align-items-end">
            <div class="col-md-4">
              <label class="form-control-label">{{ $t("member_coins.liability.month") }}</label>
              <el-select v-model="month" @change="loadData" class="w-100">
                <el-option
                  v-for="opt in months"
                  :key="opt.value"
                  :label="opt.label"
                  :value="opt.value"
                ></el-option>
              </el-select>
            </div>
            <div class="col-md-8 text-right">
              <base-button size="sm" type="primary" :loading="loading" @click="loadData">
                <i class="fa fa-sync"></i> {{ $t("refresh") || "Refresh" }}
              </base-button>
              <base-button size="sm" type="success" :loading="exporting" @click="exportXlsx">
                <i class="fa fa-file-excel"></i> {{ $t("member_coins.liability.export") }}
              </base-button>
            </div>
          </div>
        </template>

        <div v-loading="loading">
          <!-- The eight cards are the month's reconciliation in order: opening,
               every movement, closing. See liabilityCards() for why the two
               member counts are on their own line below instead. -->
          <div class="row">
            <div class="col-lg-3 col-md-6" v-for="card in cards" :key="card.key">
              <div class="card card-stats mb-3">
                <div class="card-body py-3">
                  <h5 class="card-title text-uppercase text-muted mb-0">{{ $t(card.key) }}</h5>
                  <span class="h2 font-weight-bold mb-0" :class="cardClass(card)">
                    {{ fmtSigned(card.value) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="row mb-3">
            <div class="col-md-12">
              <span class="text-muted mr-4">
                {{ $t("member_coins.liability.members_with_balance") }}:
                <b>{{ fmtNum(data.members_with_balance) }}</b>
              </span>
              <span class="text-muted">
                {{ $t("member_coins.liability.expiring_next_month") }}:
                <b>{{ fmtNum(data.expiring_next_month) }}</b>
              </span>
            </div>
          </div>

          <h6 class="heading-small text-muted mb-2">
            {{ $t("member_coins.liability.top_title") }}
          </h6>
          <hr class="mt-0 mb-3" />
          <el-table :data="top" stripe :empty-text="$t('member_coins.liability.no_data')">
            <el-table-column :label="$t('member_coins.liability.col_rank')" width="80" align="right">
              <template slot-scope="{$index}">{{ $index + 1 }}</template>
            </el-table-column>
            <el-table-column :label="$t('member_coins.liability.col_member')" min-width="120">
              <template slot-scope="{row}">
                <router-link
                  v-if="row.member_id"
                  :to="'/members/' + row.member_id"
                  class="font-weight-bold text-primary"
                >#{{ row.member_id }}</router-link>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('member_coins.liability.col_name')" min-width="260">
              <template slot-scope="{row}">{{ row.name || "-" }}</template>
            </el-table-column>
            <el-table-column :label="$t('member_coins.liability.col_coin_balance')" align="right" min-width="140">
              <template slot-scope="{row}">{{ fmtNum(row.coin_balance) }}</template>
            </el-table-column>
          </el-table>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
// Coin liability report (coin D3-4 — bo-fn#294, spec
// docs/specs/2026-09-30-coin-d3-ui-design.md §3, §4 "bo-fn").
//
// Every figure on this page comes from the API, which computes the month from
// member_coin_log rather than from member.coin_balance so that an earlier month
// can still be read correctly. This page therefore adds nothing up itself: it
// orders, labels and formats. The month string is whatever the picker produced
// ('YYYY-MM'), which is both the ?month= parameter and the export filename.
import { Table, TableColumn, Select, Option } from "element-ui";
import swal from "sweetalert2";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import MemberProvider from "@/resource/member";
import { apiErrorMessage } from "@/util/api-error";
import { liabilityCards, monthOptions } from "@/utils/memberCoins";

const member = new MemberProvider();

export default {
  name: "MemberCoinsReport",
  components: {
    RouteBreadCrumb,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    const months = monthOptions(new Date(), 12);
    return {
      loading: false,
      exporting: false,
      months,
      month: months.length ? months[0].value : "",
      data: {},
      top: [],
    };
  },
  computed: {
    cards() {
      return liabilityCards(this.data);
    },
  },
  mounted() {
    if (this.month) this.loadData();
  },
  methods: {
    fmtNum(v) {
      if (v === null || v === undefined || v === "") return "-";
      const n = Number(v);
      return Number.isFinite(n) ? n.toLocaleString("en-US") : "-";
    },
    // A movement card has to read as a direction, so an inflow keeps its '+'.
    // Outflows already arrive negative from the API and are never re-signed
    // here (that would double the sign and break the reconciliation).
    fmtSigned(v) {
      const n = Number(v) || 0;
      return (n > 0 ? "+" : "") + n.toLocaleString("en-US");
    },
    cardClass(card) {
      // Only the movements are coloured. The opening and closing balances are
      // states, not directions, and colouring them would suggest the closing
      // figure is "good" or "bad".
      const isBalance = /_(balance)$/.test(card.key);
      if (isBalance) return "";
      const n = Number(card.value) || 0;
      if (n > 0) return "text-success";
      if (n < 0) return "text-danger";
      return "text-muted";
    },
    async loadData() {
      if (!this.month) return;
      this.loading = true;
      try {
        const res = await member.AdminCoinsLiability({ month: this.month });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.data = d;
        this.top = Array.isArray(d.top) ? d.top : [];
      } catch (e) {
        this.data = {};
        this.top = [];
        swal.fire({
          title: apiErrorMessage(e, this.$t.bind(this)),
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
      }
      this.loading = false;
    },
    async exportXlsx() {
      if (!this.month) return;
      this.exporting = true;
      try {
        const res = await member.AdminCoinsLiabilityExport({ month: this.month });
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `coin-liability-${this.month}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        swal.fire({
          title: apiErrorMessage(e, this.$t.bind(this)),
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
      }
      this.exporting = false;
    },
  },
};
</script>
